/** Os cortes como trabalho da sessão.
 *
 *  Planos de corte, combinações e regras de estilo viviam só em
 *  memória: recarregar a página ou abrir a sessão noutro dia perdia-os,
 *  e uma combinação com nome não vale nada se desaparece. Passam a ficar
 *  numa chave de trabalho (`vn2r.cortes`), como as vistas 2D: vão no
 *  ficheiro de sessão (local_work), na sessão partilhada e na rede de
 *  recuperação, e saem com o "Fechar sessão".
 *
 *  O estilo base (fundo e trama) não vem aqui: é preferência de quem
 *  desenha, e fica no browser. */

import { useAppStore } from '@core/store'
import { aoReporTrabalho, getRecoveryStatus, isWorkReadBlocked, markWorkDirty } from '@core/session/recovery'
import { CORTES_KEY } from '@core/session/recoveryManifest'
import type { CombinacaoDeCorte, PlanoDeCorte } from './planosDeCorte'
import { lerRegras, type RegraDeCorte } from './estilosDeCorte'

export interface CortesGuardados {
  planos: PlanoDeCorte[]
  combinacoes: CombinacaoDeCorte[]
  regras: RegraDeCorte[]
}

const VERSAO = 1

export function serializarCortes(c: CortesGuardados): string | null {
  if (c.planos.length === 0 && c.combinacoes.length === 0 && c.regras.length === 0) return null
  return JSON.stringify({ versao: VERSAO, ...c })
}

function vec3(x: unknown): [number, number, number] | null {
  if (!Array.isArray(x) || x.length !== 3 || !x.every((n) => typeof n === 'number' && Number.isFinite(n))) return null
  return [x[0], x[1], x[2]]
}

/** Lê o que vem do armazenamento ou de uma sessão. O que não presta
 *  fica de fora, nunca rebenta. */
export function lerCortes(raw: string | null): CortesGuardados | null {
  if (!raw) return null
  let o: unknown
  try {
    o = JSON.parse(raw)
  } catch {
    return null
  }
  if (!o || typeof o !== 'object') return null
  const r = o as Record<string, unknown>
  const planos: PlanoDeCorte[] = []
  for (const p of Array.isArray(r.planos) ? r.planos : []) {
    if (!p || typeof p !== 'object') continue
    const q = p as Record<string, unknown>
    const normal = vec3(q.normal)
    const ponto = vec3(q.ponto)
    if (typeof q.id !== 'string' || !normal || !ponto) continue
    planos.push({
      id: q.id,
      nome: typeof q.nome === 'string' ? q.nome : 'Corte',
      normal,
      ponto,
      activo: q.activo !== false,
      ...(q.mostrar === false ? { mostrar: false } : {}),
      ...(q.travado === true ? { travado: true } : {}),
    })
  }
  const ids = new Set(planos.map((p) => p.id))
  const combinacoes: CombinacaoDeCorte[] = []
  for (const c of Array.isArray(r.combinacoes) ? r.combinacoes : []) {
    if (!c || typeof c !== 'object') continue
    const q = c as Record<string, unknown>
    if (typeof q.id !== 'string' || !Array.isArray(q.planos)) continue
    const dela = q.planos.filter((x): x is string => typeof x === 'string' && ids.has(x))
    if (dela.length === 0) continue
    combinacoes.push({ id: q.id, nome: typeof q.nome === 'string' ? q.nome : 'Combinação', planos: dela })
  }
  return { planos, combinacoes, regras: lerRegras(r.regras) }
}

function lerDoArmazenamento(): string | null {
  try {
    return localStorage.getItem(CORTES_KEY)
  } catch {
    return null
  }
}

function escrever(raw: string | null): void {
  try {
    if (raw === null) localStorage.removeItem(CORTES_KEY)
    else localStorage.setItem(CORTES_KEY, raw)
  } catch {
    /* armazenamento cheio ou bloqueado: dura esta sessão */
  }
}

function normalizado(raw: string | null): string | null {
  const c = lerCortes(raw)
  return c ? serializarCortes(c) : null
}

/** Repõe no store o que está no armazenamento. */
export function reporCortesDoArmazenamento(): void {
  const c = lerCortes(lerDoArmazenamento())
  useAppStore.getState().reporCortes(c ?? { planos: [], combinacoes: [], regras: [] })
}

/** Liga o store ao armazenamento. Devolve o desligar. */
export function ligarCortesASessao(): () => void {
  let temporizador: ReturnType<typeof setTimeout> | null = null
  // Uma recuperação aceite antes de o viewer montar: o conteúdo já está
  // no armazenamento, falta trazê-lo para o store.
  const s0 = useAppStore.getState()
  if (getRecoveryStatus().adopted && s0.planosDeCorte.length === 0 && s0.regrasDeCorte.length === 0) {
    reporCortesDoArmazenamento()
  }
  const largarReposicao = aoReporTrabalho(reporCortesDoArmazenamento)
  const largarStore = useAppStore.subscribe((s, antes) => {
    if (
      s.planosDeCorte === antes.planosDeCorte &&
      s.combinacoesDeCorte === antes.combinacoesDeCorte &&
      s.regrasDeCorte === antes.regrasDeCorte
    ) {
      return
    }
    if (temporizador) clearTimeout(temporizador)
    // Arrastar um plano muda o store a cada movimento: escreve-se no fim.
    temporizador = setTimeout(() => {
      temporizador = null
      // Ainda a decidir se se recupera a sessão anterior: escrever agora
      // estragava o que se vai perguntar.
      if (isWorkReadBlocked()) return
      const st = useAppStore.getState()
      // Os dois lados passam pela mesma leitura antes de se comparar: o
      // que se acabou de repor pode vir com as chaves por outra ordem, e
      // não é trabalho novo (uma sessão acabada de abrir não fica "por
      // gravar").
      const raw = normalizado(
        serializarCortes({
          planos: st.planosDeCorte,
          combinacoes: st.combinacoesDeCorte,
          regras: st.regrasDeCorte,
        }),
      )
      if (raw === normalizado(lerDoArmazenamento())) return
      escrever(raw)
      markWorkDirty()
    }, 300)
  })
  return () => {
    if (temporizador) clearTimeout(temporizador)
    largarReposicao()
    largarStore()
  }
}
